/**
 * DraggableBenchItem — wraps a single bench player row to make it both
 * a drag SOURCE (bench → floor) and a drag TARGET (floor → bench).
 *
 * Used by BenchDrawer when a `renderItem` prop is provided. Click is also
 * preserved (calls onPromote) so the user has both interactions.
 *
 * The drop swap callback is `onSwap(droppedFloorPlayer, thisBenchPlayer)`
 * — the parent (StatsRouter) is responsible for the actual roster mutation.
 */

import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Avatar, Typography, Tooltip, ButtonBase } from '@mui/material';
import { useStatsTheme, withAlpha } from '../../theme/statsTheme';
import { ITEM_TYPE_PLAYER, PLAYER_SOURCES } from './DndStatsProvider';

export default function DraggableBenchItem({
  player,
  collapsed = false,
  onPromote,
  onSwap,
}) {
  const theme = useStatsTheme();

  // Bench item is the drag source (bench → floor)
  const [{ isDragging }, dragRef, dragPreview] = useDrag(() => ({
    type: ITEM_TYPE_PLAYER,
    item: { player, source: PLAYER_SOURCES.BENCH },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }), [player]);

  // Bench item is also a drop target — accepts a floor player to swap with
  const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
    accept: ITEM_TYPE_PLAYER,
    canDrop: (dragItem) => dragItem.source === PLAYER_SOURCES.FLOOR,
    drop: (dragItem) => {
      if (typeof onSwap === 'function') onSwap(dragItem.player, player);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }), [player, onSwap]);

  const isHighlighted = isOver && canDrop;

  // Hide the native HTML5 drag preview — StatsCustomDragLayer paints its own
  React.useEffect(() => {
    if (typeof dragPreview === 'function') {
      const empty = new Image();
      empty.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
      dragPreview(empty);
    }
  }, [dragPreview]);

  return (
    <ButtonBase
      ref={(node) => {
        dragRef(node);
        dropRef(node);
      }}
      onClick={() => typeof onPromote === 'function' && onPromote(player)}
      aria-label={`promote ${player.nom}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: collapsed ? 0 : 1,
        px: collapsed ? 0 : 0.8,
        py: 0.6,
        borderRadius: theme.radius.md,
        backgroundColor: isHighlighted ? withAlpha(theme.actionColors.warning, 0.18) : theme.surfaces.floating,
        border: `1px solid ${isHighlighted ? theme.actionColors.warning : theme.surfaces.border}`,
        justifyContent: collapsed ? 'center' : 'flex-start',
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        transform: isHighlighted ? 'scale(1.02)' : 'none',
        transition: `border-color ${theme.motion.fast}, background-color ${theme.motion.fast}, transform ${theme.motion.fast}`,
        '&:hover': {
          borderColor: theme.accentBorder,
          backgroundColor: theme.accentSofter,
        },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Tooltip title={collapsed ? player.nom : ''}>
        <Avatar
          src={player.avatarUrl}
          sx={{
            width: collapsed ? 36 : 32,
            height: collapsed ? 36 : 32,
            border: `1px solid ${theme.surfaces.border}`,
            backgroundColor: theme.surfaces.elevated,
            fontSize: 12,
          }}
        >
          {player.nom?.charAt(0).toUpperCase() || '?'}
        </Avatar>
      </Tooltip>
      {!collapsed && (
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          {player.dorsal && (
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: theme.accent, letterSpacing: '0.04em' }}>
              #{player.dorsal}
            </Typography>
          )}
          <Typography
            noWrap
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: theme.text.primary,
            }}
          >
            {player.nom}
          </Typography>
        </Box>
      )}
    </ButtonBase>
  );
}
